import { apiClient } from './api-client'

export type DeadlineUnit = 'UTEIS' | 'CORRIDOS'
export type Natureza = 'FERIADO' | 'PONTO_FACULTATIVO' | 'SUSPENSAO' | 'RECESSO'
export type Efeito = 'SEM_EXPEDIENTE' | 'PRAZOS_SUSPENSOS' | 'EXPEDIENTE_REDUZIDO'

export interface MemoryReason {
  descricao: string
  natureza?: Natureza
  efeito?: Efeito
  fonteUrl?: string | null
  aConferir?: boolean
}

export interface MemoryLine {
  data: string
  diaSemana: string
  evento: 'DISPONIBILIZACAO' | 'PUBLICACAO' | 'INICIO' | 'CONTADO' | 'PULADO' | 'PRORROGADO' | 'VENCIMENTO' | 'PRAZO_INTERNO'
  contagem?: number
  fase?: 'PUBLICACAO' | 'INICIO' | 'CONTAGEM' | 'VENCIMENTO'
  motivos: MemoryReason[]
}

/** Memória do cálculo gravada pela API (ver módulo deadlines). */
export interface CalculationMemory {
  versao: 1
  tribunal: string
  uf: string | null
  unidade: DeadlineUnit
  dias: number
  diasPrazoInterno: number
  disponibilizacao: string
  publicacao: string
  inicio: string
  /** Fatal adotado: sempre a leitura mais segura (sem os dias "a conferir"/expediente reduzido, se vencer antes). */
  vencimento: string
  /** Conta legal (com os dias "a conferir"), só quando mais tarde que o fatal; informativa. */
  vencimentoLegal: string | null
  prazoInterno: string
  fundamentos: string[]
  linhaDoTempo: MemoryLine[]
  diasPulados: Array<{ data: string; diaSemana: string; fase: string; motivos: MemoryReason[] }>
  avisos: string[]
  calculadoEm: string
}

export interface DeadlineCalculation {
  publicacao: string
  inicio: string
  vencimento: string
  vencimentoLegal: string | null
  prazoInterno: string
  memoria: CalculationMemory
}

export interface DeadlineRecord {
  id: string
  status: 'SUGERIDO' | 'CONFIRMADO' | 'DESCARTADO'
  origem: 'PUBLICACAO' | 'MANUAL'
  publicationId: number | null
  caseId: string | null
  numeroProcesso: string | null
  tribunal: string
  uf: string | null
  comarca: string | null
  dataDisponibilizacao: string
  dias: number
  unidade: DeadlineUnit
  diasPrazoInterno: number
  dataPublicacao: string
  dataInicio: string
  prazoFatal: string
  /** Conta legal (com os dias "a conferir"), só quando mais tarde que o fatal; informativa. */
  prazoFatalLegal: string | null
  prazoInterno: string
  memoria: CalculationMemory
  trechoDetectado: string | null
  taskId: number | null
  confirmedAt: string | null
  createdAt: string
}

export interface CalculateInput {
  tribunal: string
  uf?: string | null
  disponibilizacao: string
  dias: number
  unidade: DeadlineUnit
  diasPrazoInterno?: number
}

export interface Tribunal {
  sigla: string
  nome: string
  ramo: 'ESTADUAL' | 'FEDERAL'
  ufs: string[]
}

export interface YearCoverage {
  tribunal: string
  ano: number
  situacao: 'COMPLETO' | 'PARCIAL'
  observacao: string
}

export interface CalendarDay {
  id: string
  data: string
  abrangencia: 'NACIONAL' | 'ESTADUAL' | 'TRIBUNAL'
  uf: string | null
  tribunal: string | null
  natureza: Natureza
  efeito: Efeito
  descricao: string
  fonteUrl: string | null
  trecho: string | null
  observacao: string | null
  aConferir: boolean
  origem: 'SEED' | 'MANUAL'
  ativo: boolean
  editadoEm: string | null
}

export interface Suspension {
  id: string
  tribunal: string
  uf: string | null
  dataInicio: string
  dataFim: string
  natureza: Natureza
  efeito: Efeito
  motivo: string
  fonteUrl: string | null
  createdAt: string
  canceladaEm: string | null
}

export type SuspensionInput = Omit<Suspension, 'id' | 'createdAt' | 'canceladaEm'>

/** Erro 409 da confirmação: o calendário mudou e a API devolve o prazo recalculado. */
export class DeadlineChangedError extends Error {
  constructor(readonly record: DeadlineRecord, message: string) {
    super(message)
  }
}

/** Prazos e calendário forense (`/api/deadlines`). O cálculo é sempre do servidor. */
export const deadlineService = {
  async calculate(input: CalculateInput): Promise<DeadlineCalculation> {
    const response = await apiClient.post('/api/deadlines/calculate', input)
    return response.data.data
  },

  async listForPublication(publicationId: number): Promise<DeadlineRecord[]> {
    const response = await apiClient.get(`/api/deadlines/by-publication/${publicationId}`)
    return response.data.data
  },

  async createManual(input: CalculateInput & { publicationId?: number | null; caseId?: string | null; numeroProcesso?: string | null }): Promise<DeadlineRecord> {
    const response = await apiClient.post('/api/deadlines', input)
    return response.data.data
  },

  async confirm(id: string, responsavelId?: string | null): Promise<DeadlineRecord> {
    try {
      const response = await apiClient.post(`/api/deadlines/${id}/confirm`, { responsavelId: responsavelId ?? null })
      return response.data.data
    } catch (error) {
      const data = (error as { response?: { status?: number; data?: { data?: DeadlineRecord; error?: { message?: string } } } }).response
      if (data?.status === 409 && data.data?.data?.status === 'SUGERIDO') {
        throw new DeadlineChangedError(data.data.data, data.data.error?.message ?? 'O cálculo mudou')
      }
      throw error
    }
  },

  async discard(id: string): Promise<DeadlineRecord> {
    const response = await apiClient.post(`/api/deadlines/${id}/discard`)
    return response.data.data
  },

  async tribunais(): Promise<{ tribunais: Tribunal[]; cobertura: YearCoverage[] }> {
    const response = await apiClient.get('/api/deadlines/tribunais')
    return response.data.data
  },

  async calendar(params: { tribunal?: string; ano: number; inativos?: boolean }): Promise<{ dias: CalendarDay[]; suspensoes: Suspension[] }> {
    const response = await apiClient.get('/api/deadlines/calendar', {
      params: { tribunal: params.tribunal || undefined, ano: params.ano, inativos: params.inativos ? '1' : undefined },
    })
    return response.data.data
  },

  async updateDay(id: string, patch: { ativo?: boolean; aConferir?: boolean }): Promise<CalendarDay> {
    const response = await apiClient.patch(`/api/deadlines/calendar/days/${id}`, patch)
    return response.data.data
  },

  async createSuspension(input: SuspensionInput): Promise<Suspension> {
    const response = await apiClient.post('/api/deadlines/suspensions', input)
    return response.data.data
  },

  async cancelSuspension(id: string): Promise<void> {
    await apiClient.delete(`/api/deadlines/suspensions/${id}`)
  },
}
